import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../lib/api'

/**
 * The signed-in user's own account. Three self-contained cards — profile, password, email —
 * each with its own save button and its own error line, so one failing form never blocks the
 * others. Email changes are confirmed by a code sent to the NEW address before they take
 * effect, so a typo can't lock anyone out.
 */
export function Settings() {
  const qc = useQueryClient()
  const { data: me, isLoading } = useQuery({ queryKey: ['account'], queryFn: api.account.get })

  if (isLoading) return <div className="skeleton" style={{ height: 200 }} />

  return (
    <div>
      <h1>Account settings</h1>
      <p className="lede">Your profile, password, and sign-in email.</p>

      <ProfileCard name={me?.name ?? ''} />
      <PasswordCard />
      <EmailCard
        email={me?.email ?? ''}
        verified={me?.email_verified ?? false}
        pending={me?.pending_email ?? null}
        onChanged={() => qc.invalidateQueries({ queryKey: ['account'] })}
      />
      <SecurityCard enabled={me?.mfa_enabled ?? false} onChanged={() => qc.invalidateQueries({ queryKey: ['account'] })} />
      <LoginHistoryCard />
    </div>
  )
}

function SecurityCard({ enabled, onChanged }: { enabled: boolean; onChanged: () => void }) {
  const [setup, setSetup] = useState<{ secret: string; otpauth_uri: string } | null>(null)
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<ApiError | null>(null)

  const begin = useMutation({ mutationFn: () => api.account.twoFactorSetup(), onSuccess: (d) => { setSetup(d); setError(null) }, onError: e => setError(e as ApiError) })
  const enable = useMutation({ mutationFn: () => api.account.twoFactorEnable({ code: code.trim() }), onSuccess: () => { setSetup(null); setCode(''); setError(null); onChanged() }, onError: e => setError(e as ApiError) })
  const disable = useMutation({ mutationFn: () => api.account.twoFactorDisable({ password }), onSuccess: () => { setPassword(''); setError(null); onChanged() }, onError: e => setError(e as ApiError) })

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="spread" style={{ alignItems: 'center' }}>
        <h2 style={{ marginTop: 0, marginBottom: 0 }}>Two-factor authentication</h2>
        {enabled ? <span className="badge ok">on</span> : <span className="badge pending">off</span>}
      </div>
      <p className="small muted">A time-based code from an authenticator app (Google Authenticator, 1Password, Authy) on top of your password.</p>
      {error && <div className="alert error" style={{ marginBottom: 12 }}>{error.message}</div>}

      {!enabled && !setup && (
        <button onClick={() => begin.mutate()} disabled={begin.isPending}>{begin.isPending ? 'Starting…' : 'Enable two-factor'}</button>
      )}

      {!enabled && setup && (
        <div>
          <p className="small">Add this secret to your authenticator app (or scan the URI), then enter the 6-digit code to confirm.</p>
          <table className="kv boxed" style={{ marginBottom: 12 }}>
            <tbody>
              <tr><td>Secret</td><td className="mono">{setup.secret}</td></tr>
              <tr><td>URI</td><td className="mono small" style={{ wordBreak: 'break-all' }}>{setup.otpauth_uri}</td></tr>
            </tbody>
          </table>
          <div className="field">
            <label>6-digit code</label>
            <input inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} style={{ maxWidth: 160, letterSpacing: 4, fontSize: 18 }} />
          </div>
          <button onClick={() => enable.mutate()} disabled={code.length !== 6 || enable.isPending}>{enable.isPending ? 'Verifying…' : 'Confirm & turn on'}</button>
        </div>
      )}

      {enabled && (
        <form onSubmit={e => { e.preventDefault(); disable.mutate() }}>
          <div className="field">
            <label>Confirm password to turn off</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" />
          </div>
          <button className="ghost danger" type="submit" disabled={!password || disable.isPending}>{disable.isPending ? 'Turning off…' : 'Turn off two-factor'}</button>
        </form>
      )}
    </div>
  )
}

function LoginHistoryCard() {
  const { data } = useQuery({ queryKey: ['security-log'], queryFn: api.account.securityLog })
  const rows = data?.data ?? []
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <h2 style={{ marginTop: 0 }}>Recent account activity</h2>
      {rows.length === 0 ? <p className="small muted">No activity recorded yet.</p> : (
        <table className="data">
          <thead><tr><th>When</th><th>Type</th><th>Result</th><th>IP</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="small" title={new Date(r.at).toLocaleString()}>
                  {new Date(r.at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                </td>
                <td className="small">{activityLabel(r.type, r.detail, r.result)}<span className="muted"> · {CHANNEL[r.type] ?? r.type}</span></td>
                <td className="small"><ResultBadge result={r.result} /></td>
                <td className="small mono">{r.ip}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function ProfileCard({ name: initial }: { name: string }) {
  const qc = useQueryClient()
  const [name, setName] = useState(initial)
  const [ok, setOk] = useState(false)
  const [error, setError] = useState<ApiError | null>(null)
  useEffect(() => setName(initial), [initial])

  const save = useMutation({
    mutationFn: () => api.account.updateProfile({ name: name.trim() || null }),
    onSuccess: () => { setOk(true); setError(null); qc.invalidateQueries({ queryKey: ['account'] }) },
    onError: (e) => { setError(e as ApiError); setOk(false) },
  })

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <h2 style={{ marginTop: 0 }}>Profile</h2>
      <form onSubmit={e => { e.preventDefault(); save.mutate() }}>
        <div className="field">
          <label htmlFor="dn">Display name</label>
          <input id="dn" placeholder="Your name" value={name} onChange={e => { setName(e.target.value); setOk(false) }} />
          <div className="hint">Shown in the dashboard. Optional.</div>
        </div>
        {error && <div className="alert error" style={{ marginBottom: 12 }}>{error.message}</div>}
        {ok && <div className="alert ok" style={{ marginBottom: 12 }}>Saved.</div>}
        <button type="submit" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button>
      </form>
    </div>
  )
}

function PasswordCard() {
  const [cur, setCur] = useState('')
  const [next, setNext] = useState('')
  const [ok, setOk] = useState(false)
  const [error, setError] = useState<ApiError | null>(null)

  const save = useMutation({
    mutationFn: () => api.account.changePassword({ currentPassword: cur, newPassword: next }),
    onSuccess: () => { setOk(true); setError(null); setCur(''); setNext('') },
    onError: (e) => { setError(e as ApiError); setOk(false) },
  })

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <h2 style={{ marginTop: 0 }}>Password</h2>
      <form onSubmit={e => { e.preventDefault(); save.mutate() }}>
        <div className="field">
          <label htmlFor="cp">Current password</label>
          <input id="cp" type="password" value={cur} onChange={e => setCur(e.target.value)} autoComplete="current-password" />
        </div>
        <div className="field">
          <label htmlFor="np">New password</label>
          <input id="np" type="password" value={next} onChange={e => setNext(e.target.value)} autoComplete="new-password" />
          <div className="hint">At least 12 characters. Length beats complexity rules.</div>
        </div>
        {error && <div className="alert error" style={{ marginBottom: 12 }}>{error.message}</div>}
        {ok && <div className="alert ok" style={{ marginBottom: 12 }}>Password changed.</div>}
        <button type="submit" disabled={!cur || next.length < 12 || save.isPending}>
          {save.isPending ? 'Changing…' : 'Change password'}
        </button>
      </form>
    </div>
  )
}

function EmailCard({
  email, verified, pending, onChanged,
}: { email: string; verified: boolean; pending: string | null; onChanged: () => void }) {
  const [newEmail, setNewEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [stage, setStage] = useState<'idle' | 'code'>(pending ? 'code' : 'idle')
  const [error, setError] = useState<ApiError | null>(null)

  const request = useMutation({
    mutationFn: () => api.account.changeEmail({ newEmail: newEmail.trim(), password }),
    onSuccess: () => { setStage('code'); setError(null); setPassword(''); onChanged() },
    onError: (e) => setError(e as ApiError),
  })
  const confirm = useMutation({
    mutationFn: () => api.account.verifyEmailChange({ code: code.trim() }),
    onSuccess: () => { setStage('idle'); setError(null); setNewEmail(''); setCode(''); onChanged() },
    onError: (e) => setError(e as ApiError),
  })

  // Verifying the CURRENT address (separate from changing it): send a code to it, then
  // enter that code. This is what turns the "unverified" badge into "verified".
  const [vStage, setVStage] = useState<'idle' | 'sent'>('idle')
  const [vCode, setVCode] = useState('')
  const [vError, setVError] = useState<ApiError | null>(null)
  const sendVerify = useMutation({
    mutationFn: () => api.auth.resendVerification({ email }),
    onSuccess: () => { setVStage('sent'); setVError(null) },
    onError: (e) => setVError(e as ApiError),
  })
  const doVerify = useMutation({
    mutationFn: () => api.auth.verifyEmail({ email, code: vCode.trim() }),
    onSuccess: () => { setVStage('idle'); setVCode(''); setVError(null); onChanged() },
    onError: (e) => setVError(e as ApiError),
  })

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <h2 style={{ marginTop: 0 }}>Email</h2>
      <table className="kv boxed" style={{ marginBottom: 16 }}>
        <tbody>
          <tr>
            <td>Current</td>
            <td className="mono">
              {email}{' '}
              {verified
                ? <span className="badge ok">verified</span>
                : <span className="badge pending">unverified</span>}
              {!verified && vStage === 'idle' && (
                <button type="button" className="ghost small" style={{ marginLeft: 10 }}
                        disabled={sendVerify.isPending}
                        onClick={() => sendVerify.mutate()}>
                  {sendVerify.isPending ? 'Sending…' : 'Verify now'}
                </button>
              )}
            </td>
          </tr>
          {pending && <tr><td>Pending</td><td className="mono">{pending} <span className="badge pending">awaiting code</span></td></tr>}
        </tbody>
      </table>

      {!verified && vStage === 'sent' && (
        <form className="verify-now" onSubmit={e => { e.preventDefault(); doVerify.mutate() }} style={{ marginBottom: 16 }}>
          <div className="alert info" style={{ marginBottom: 12 }}>
            We sent a 6-digit code to <strong>{email}</strong>. Enter it below to verify your address.
          </div>
          <div className="field">
            <label htmlFor="vc">Verification code</label>
            <input id="vc" inputMode="numeric" maxLength={6} placeholder="123456" value={vCode}
                   onChange={e => setVCode(e.target.value.replace(/\D/g, ''))}
                   style={{ maxWidth: 160, letterSpacing: 4, fontSize: 20 }} />
          </div>
          {vError && <div className="alert error" style={{ marginBottom: 12 }}>{vError.message}</div>}
          <div className="row" style={{ gap: 8 }}>
            <button type="submit" disabled={vCode.length !== 6 || doVerify.isPending}>
              {doVerify.isPending ? 'Verifying…' : 'Verify email'}
            </button>
            <button type="button" className="ghost small" disabled={sendVerify.isPending}
                    onClick={() => sendVerify.mutate()}>
              {sendVerify.isPending ? 'Resending…' : 'Resend code'}
            </button>
          </div>
        </form>
      )}

      {stage === 'idle' && (
        <form onSubmit={e => { e.preventDefault(); request.mutate() }}>
          <div className="field">
            <label htmlFor="ne">New email</label>
            <input id="ne" type="email" placeholder="you@company.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="pw">Confirm with your password</label>
            <input id="pw" type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" />
          </div>
          {error && <div className="alert error" style={{ marginBottom: 12 }}>{error.message}</div>}
          <button type="submit" disabled={!newEmail || !password || request.isPending}>
            {request.isPending ? 'Sending code…' : 'Change email'}
          </button>
        </form>
      )}

      {stage === 'code' && (
        <form onSubmit={e => { e.preventDefault(); confirm.mutate() }}>
          <div className="alert info" style={{ marginBottom: 12 }}>
            We sent a 6-digit code to <strong>{pending ?? newEmail}</strong>. Enter it to confirm.
          </div>
          <div className="field">
            <label htmlFor="oc">Verification code</label>
            <input id="oc" inputMode="numeric" maxLength={6} placeholder="123456" value={code}
                   onChange={e => setCode(e.target.value.replace(/\D/g, ''))} style={{ maxWidth: 160, letterSpacing: 4, fontSize: 20 }} />
          </div>
          {error && <div className="alert error" style={{ marginBottom: 12 }}>{error.message}</div>}
          <button type="submit" disabled={code.length !== 6 || confirm.isPending}>
            {confirm.isPending ? 'Confirming…' : 'Confirm new email'}
          </button>
          <button type="button" className="ghost" style={{ marginLeft: 8 }} onClick={() => { setStage('idle'); setError(null) }}>
            Cancel
          </button>
        </form>
      )}
    </div>
  )
}

const CHANNEL: Record<string, string> = { Dashboard: 'Web dashboard', ApiKey: 'API key', Smtp: 'SMTP' }

const RESULT: Record<string, { text: string; cls: string }> = {
  Success: { text: 'Successful', cls: 'ok' },
  BadCredentials: { text: 'Incorrect credentials', cls: 'failed' },
  Suspended: { text: 'Account suspended', cls: 'failed' },
  RateLimited: { text: 'Too many attempts', cls: 'pending' },
  DomainNotAllowed: { text: 'Blocked', cls: 'failed' },
}

function ResultBadge({ result }: { result: string }) {
  const r = RESULT[result] ?? { text: result.replace(/([a-z])([A-Z])/g, '$1 $2'), cls: 'info' }
  return <span className={`badge ${r.cls}`}>{r.text}</span>
}

/** Plain-language description of a security event. */
function activityLabel(type: string, detail?: string | null, result?: string): string {
  const d = (detail ?? '').toLowerCase()
  if (d === 'signup') return 'Account created'
  if (d === 'bad_mfa') return 'Two-factor code rejected'
  if (d.includes('password_reset') || d.includes('reset')) return 'Password reset'
  if (d.includes('locked')) return 'Sign-in blocked (account locked)'
  if (d.includes('unverified')) return 'Sign-in pending email verification'
  if (type === 'Dashboard') return result === 'Success' ? 'Signed in' : 'Sign-in attempt'
  if (type === 'ApiKey') return result === 'Success' ? 'API request authorised' : 'API key rejected'
  if (type === 'Smtp') return result === 'Success' ? 'SMTP login' : 'SMTP login rejected'
  return detail ? detail.replace(/_/g, ' ') : 'Activity'
}
